import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Box, InputAdornment,
  CircularProgress, Chip, ToggleButton, ToggleButtonGroup,
  Checkbox, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  MenuItem, Select, InputLabel, FormControl, Divider,
} from "@mui/material";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import Tooltip from "@mui/material/Tooltip";
import { apiGet, apiPost, ApiError } from "../api/client";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function ScoreChip({ score }) {
  const pct = Math.round((score || 0) * 100);
  if (score >= 0.95) return <Chip size="small" label={`Strong • ${pct}%`} color="success" variant="outlined" />;
  if (score >= 0.6)  return <Chip size="small" label={`Maybe • ${pct}%`} color="warning" variant="outlined" />;
  return <Chip size="small" label={`${pct}%`} variant="outlined" />;
}

// Free-text condition on items.condition is normalized to one of these
// four for the picker. The backend's _CONDITION_LABEL_TO_NOTES / choose_condition
// path also accepts these forms. Backend maps these labels to the FB-side
// condition dropdown via condition_from_notes + map_condition_to_facebook.
const FB_CONDITIONS = ["New", "Open Box", "Used", "For Parts"];

function normalizeCondition(c) {
  const s = String(c || "").toLowerCase();
  if (!s) return "New";
  if (s.includes("part")) return "For Parts";
  if (s.includes("open box") || s.includes("like new") || s === "new_other") return "Open Box";
  if (s.startsWith("new")) return "New";
  return "Used";
}

// Extract the initial image URL list from a candidate row. ``image_urls``
// arrives as a JSON-array string on the wire; be defensive against bare
// strings / null / already-parsed arrays so a malformed row can still be
// edited instead of the modal crashing empty.
function parseImageUrls(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((u) => typeof u === "string" && u.trim());
  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith("[")) {
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr)) return arr.filter((u) => typeof u === "string" && u.trim());
    } catch { /* fallthrough */ }
  }
  return [s];
}

// "Edit & Publish" modal — one-time per-publish overrides for title,
// price, description, condition, and image URLs. Nothing here writes
// back to items.*; the backend applies overrides in-memory only.
function EditPublishDialog({ open, candidate, defaultPrice, onClose, onPublish, publishing }) {
  const [title, setTitle]           = useState("");
  const [price, setPrice]           = useState("");
  const [description, setDescription] = useState("");
  const [condition, setCondition]   = useState("New");
  const [urls, setUrls]             = useState([]);
  const [newUrl, setNewUrl]         = useState("");

  // Reseed the modal state whenever the underlying candidate changes so
  // opening the same modal for a different row shows the right data.
  useEffect(() => {
    if (!open || !candidate) return;
    setTitle(candidate.cleaned_title || candidate.title || "");
    setPrice(defaultPrice != null ? String(defaultPrice) : "");
    setDescription(""); // blank = use backend auto-generated description
    setCondition(normalizeCondition(candidate.condition));
    setUrls(parseImageUrls(candidate.image_urls));
    setNewUrl("");
  }, [open, candidate, defaultPrice]);

  const priceNum = Number(price);
  const canPublish = Boolean(candidate) && Number.isFinite(priceNum) && priceNum > 0 && urls.length > 0 && title.trim();

  const move = (idx, dir) => {
    setUrls((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const addUrl = () => {
    const u = newUrl.trim();
    if (!u) return;
    setUrls((prev) => [...prev, u]);
    setNewUrl("");
  };

  const submit = () => {
    if (!canPublish) return;
    onPublish({
      title: title.trim(),
      price: priceNum,
      description: description.trim(),  // blank => backend uses default
      condition,
      image_urls: urls,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Edit & Publish
        <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontFamily: "mono" }}>
          {candidate?.lot_id}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Photos */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Photos ({urls.length})</Typography>
            {urls.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Add at least one photo URL to publish.
              </Typography>
            )}
            <Stack spacing={0.75}>
              {urls.map((u, i) => (
                <Stack key={`${u}-${i}`} direction="row" spacing={1} alignItems="center">
                  <Box
                    component="img"
                    src={u}
                    alt=""
                    sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: 0.5, bgcolor: "grey.100" }}
                    onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                  />
                  <Typography variant="caption" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "mono" }}>
                    {i + 1}. {u}
                  </Typography>
                  <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={i === urls.length - 1} onClick={() => move(i, 1)}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setUrls((prev) => prev.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://…/image.jpg"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
              />
              <Button variant="outlined" size="small" onClick={addUrl} startIcon={<AddPhotoAlternateOutlinedIcon fontSize="small" />}>
                Add
              </Button>
            </Stack>
          </Box>
          <Divider />
          {/* Title */}
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 100))}
            helperText={`${title.length}/100`}
            fullWidth
          />
          {/* Price + Condition */}
          <Stack direction="row" spacing={1}>
            <TextField
              label="Price"
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Condition</InputLabel>
              <Select
                label="Condition"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
              >
                {FB_CONDITIONS.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {/* Description */}
          <TextField
            label="Description (blank = auto)"
            size="small"
            multiline
            minRows={4}
            maxRows={10}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Leave blank to use the auto-generated description (title + notes + pickup blurb)."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publishing}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canPublish || publishing}
          startIcon={publishing ? <CircularProgress size={14} color="inherit" /> : <CheckOutlinedIcon fontSize="small" />}
        >
          {publishing ? "Publishing…" : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CandidateRow({ candidate, onPublish, onEdit, onSkipped, busy }) {
  const isPublishing = busy === "publish";
  const isSkipping   = busy === "skip";
  const isExternal   = candidate.source === "orderchecker";

  // Default price: fb_value if set, else value. User can edit before listing.
  // External OC rows carry no price data — user has to type one in.
  const suggested = candidate.fb_value > 0 ? candidate.fb_value : candidate.value;
  const [price, setPrice] = useState(
    suggested > 0 ? String(Math.round(suggested)) : ""
  );

  // Reset price if the underlying suggestion changes (rare, e.g. a refresh).
  useEffect(() => {
    setPrice(suggested > 0 ? String(Math.round(suggested)) : "");
  }, [suggested]);

  // Editable title. Prefill from cleaned_title (FB-safe) → title → "".
  // FB caps the title at 100 chars, so we mirror that here as a soft
  // cap so the user sees the effective length while typing. Backend
  // also clips defensively.
  const originalTitle = candidate.cleaned_title || candidate.title || "";
  const [title, setTitle] = useState(originalTitle);
  useEffect(() => { setTitle(candidate.cleaned_title || candidate.title || ""); },
    [candidate.cleaned_title, candidate.title]);
  const titleTrimmed = title.trim();
  const titleChanged = titleTrimmed && titleTrimmed !== originalTitle.trim();

  const priceNum = Number(price);
  const priceValid = Number.isFinite(priceNum) && priceNum > 0;

  const onSubmit = (e) => {
    e?.preventDefault?.();
    if (!priceValid) return;
    // Pass the edited title through — the caller forwards it to
    // /api/fb_publish, which updates items.title in place before
    // spawning the CLI. Only send when non-empty to preserve the
    // "no override" backend semantics.
    onPublish(priceNum, titleTrimmed || null);
  };

  return (
    <TableRow hover>
      <TableCell sx={{ width: 84 }}>
        <ImageGalleryCell imageUrlsStr={candidate.image_urls} thumbnailSize={64} />
      </TableCell>
      <TableCell sx={{ maxWidth: 480, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
          {isExternal ? (
            <Chip size="small" color="secondary" label={
              candidate.available_qty > 1
                ? `OrderChecker • ${candidate.available_qty} on hand`
                : "OrderChecker"
            } />
          ) : (
            <ScoreChip score={candidate.score} />
          )}
          {!isExternal && candidate.reasons?.slice(0, 3).map((r) => {
            const isNeg = r.startsWith("~");
            return (
              <Chip
                key={r}
                size="small"
                variant="outlined"
                color={isNeg ? "error" : "default"}
                label={isNeg ? `not: ${r.slice(1)}` : r}
              />
            );
          })}
          {candidate.listed_ebay && (
            <Chip size="small" variant="outlined" color="primary" label="on eBay" />
          )}
          {candidate.purchased_at && !isExternal && (
            <Chip size="small" variant="outlined" label={candidate.purchased_at} />
          )}
        </Stack>
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          variant="standard"
          fullWidth
          multiline
          maxRows={2}
          inputProps={{ maxLength: 100, 'aria-label': 'FB listing title' }}
          placeholder={originalTitle || "Title used on Facebook"}
          helperText={titleChanged
            ? `Edited (${titleTrimmed.length}/100) — will overwrite the stored title on publish`
            : `${(titleTrimmed || originalTitle).length}/100`}
          FormHelperTextProps={{ sx: { m: 0, fontSize: 10.5 } }}
          sx={{
            '& .MuiInputBase-input': { fontSize: 13.5, lineHeight: 1.4 },
            '& .MuiInput-underline:before': { borderBottomStyle: 'dotted' },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'mono', display: 'block', mt: 0.25 }}>
          {candidate.lot_id}
          {candidate.condition && (
            <Box component="span" sx={{ ml: 1 }}>{candidate.condition}</Box>
          )}
          {candidate.location && (
            <Box component="span" sx={{ ml: 1 }}>@ {candidate.location}</Box>
          )}
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1, color: 'text.secondary', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          {candidate.spend > 0 && <Box component="span">Cost {money(candidate.spend)}</Box>}
          {candidate.value > 0 && <Box component="span">eBay {money(candidate.value)}</Box>}
          {candidate.fb_value > 0 && (
            <Box component="span" sx={{ color: 'primary.main' }}>FB suggest {money(candidate.fb_value)}</Box>
          )}
          {candidate.category_name && <Box component="span">{candidate.category_name}</Box>}
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 180, verticalAlign: 'top' }}>
        <Stack spacing={1} sx={{ width: 160 }}>
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={1}>
              <TextField
                size="small"
                type="number"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputProps={{ min: 1, step: 1, 'aria-label': 'FB list price' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <Stack direction="row" spacing={0.5}>
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={(!!busy && !isPublishing) || !priceValid || isPublishing}
                  startIcon={isPublishing ? <CircularProgress size={14} color="inherit" /> : <CheckOutlinedIcon fontSize="small" />}
                  sx={{ flex: 1 }}
                >
                  {isPublishing ? "Publishing…" : "Publish now"}
                </Button>
                <Tooltip title="Edit photos, title, price, description, condition before publishing">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!!busy}
                      onClick={onEdit}
                      sx={{ border: 1, borderColor: "divider" }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
          <Tooltip title={isExternal
            ? "Hide this OrderChecker item from FB Candidates permanently. Undo from Settings → Skipped."
            : "Hide this lot from FB Candidates permanently. Undo from Settings → Skipped."}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={(!!busy && !isSkipping) || isSkipping}
              startIcon={isSkipping ? <CircularProgress size={14} color="inherit" /> : <CloseOutlinedIcon fontSize="small" />}
              onClick={onSkipped}
              fullWidth
            >
              {isSkipping ? "Hiding…" : "Hide"}
            </Button>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default function FBCandidates() {
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});         // { lot_id: "publish"|"skip" }
  const [search, setSearch] = useState("");
  // Default to "Strong + maybe" (0.3) so the user sees a broad set up front.
  // The previous default of 0.5 hid medium-confidence candidates and items
  // whose only signal was a high-value bump.
  const [minScore, setMinScore] = useState(0.3);
  // Time-window filter on items.purchase_date (unix seconds). 0 = no filter,
  // otherwise the cutoff is now - N days. Defaults to "all" so the operator
  // sees everything by default.
  const [windowDays, setWindowDays] = useState(0);
  // Toggle: switch the list to EXCLUSIVELY show buyer-side OrderChecker
  // delivered inventory. Off = normal BidRL candidates; on = only OC rows.
  const [onlyExternal, setOnlyExternal] = useState(false);
  // Minimum "recommended listing price" filter. Compared to the same
  // ``suggested`` value the row uses to prefill the FB price field —
  // ``fb_value`` when set, otherwise ``value`` (eBay list). 0 = no
  // filter. Rows with no price data (e.g. OC rows without a recorded
  // fb_value/value) are hidden when the threshold is > 0 because we
  // can't confirm they meet it; drop the threshold back to 0 to see
  // them again.
  const [minListingPrice, setMinListingPrice] = useState(0);
  const [queueCounts, setQueueCounts] = useState({ queued: 0, exported: 0, listed: 0, error: 0 });

  // "Edit & Publish" modal — holds the candidate whose row's edit icon was
  // clicked, plus its suggested price so the modal opens pre-filled. Null
  // when the modal is closed.
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [editingBusy, setEditingBusy] = useState(false);

  // Manual publish — for items that don't surface as candidates (e.g. a
  // keyword the scorer doesn't yet recognize). The lot still has to exist
  // in the items table; backend rejects unknown lot_ids.
  const [manualLot, setManualLot] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [manualIgnoreLocation, setManualIgnoreLocation] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);

  const loadQueueStatus = useCallback(async (signal) => {
    try {
      const js = await apiGet("/api/fb_queue_status", { signal });
      if (js?.success && js.counts) setQueueCounts(js.counts);
    } catch {
      // best-effort; ignore
    }
  }, []);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const url = `/api/fb_candidates?min_score=${minScore}${onlyExternal ? "&only_external=1" : ""}`;
      const js = await apiGet(url, { signal });
      setItems(Array.isArray(js?.results) ? js.results : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load candidates", e instanceof ApiError ? e.message : String(e));
      setItems([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [minScore, onlyExternal, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    loadQueueStatus(ctrl.signal);
    return () => ctrl.abort();
  }, [load, loadQueueStatus]);

  const setRowBusy = (lotId, action) =>
    setBusy((prev) => {
      const n = { ...prev };
      if (action) n[lotId] = action;
      else delete n[lotId];
      return n;
    });

  // Publish via the single-item lister (facebook/single_list.py). The
  // /api/fb_publish endpoint is now ASYNC — it kicks off a detached
  // subprocess and returns immediately, so we don't block the request
  // (which would 504 through whatever proxy is in front of Flask). We
  // then poll /api/fb_publish/status every 5s until fb_queue.status
  // flips to 'listed' or 'error', or we hit the 10-minute safety cap.
  // Core publish flow — used by both the per-row candidate button and the
  // manual lot_id form. ``onSettled(status)`` is called whether the publish
  // succeeded, failed, or timed out so the caller can clear its busy state.
  const startPublish = useCallback(async (lotId, price, { onSettled, ignoreLocation, external, title, overrides } = {}) => {
    try {
      const body = { lot_id: lotId };
      if (Number.isFinite(price) && price > 0) body.price = price;
      if (ignoreLocation) body.ignore_location = true;
      // External (OrderChecker) rows: forward title/image so the backend
      // can materialize an items row before spawning the publish CLI.
      if (external) body.external = external;
      // Per-publish title override — Flask updates items.title +
      // cleaned_title (or promotes the OC row with this title) before
      // spawning the CLI. Empty/omitted keeps the stored title.
      if (typeof title === "string" && title.trim()) body.title = title.trim();
      // Full per-publish overrides bag from the Edit & Publish modal.
      // Nothing here is persisted to items.*; the CLI applies them
      // in-memory only for this one publish.
      if (overrides && typeof overrides === "object") body.overrides = overrides;
      const res = await apiPost("/api/fb_publish", body);
      if (!res?.success) {
        toastError("Couldn't start publish", res?.error || "Unknown error");
        onSettled?.("error");
        return;
      }
      toastInfo("Publishing on FB…", `${lotId} — Chrome opens in the background (30-120s)`);

      const start = Date.now();
      const poll = async () => {
        try {
          const s = await apiGet(`/api/fb_publish/status?lot_id=${encodeURIComponent(lotId)}`);
          if (s?.status === "listed" || s?.fb_listed) {
            toastSuccess("Published to FB", lotId);
            setItems((prev) => prev.filter((c) => c.lot_id !== lotId));
            loadQueueStatus();
            onSettled?.("listed");
            return;
          }
          if (s?.status === "error") {
            const reason = s?.error_message || `check fb_publish_logs/${lotId}_*.log`;
            toastError("Publish failed", `${lotId} — ${reason}`);
            loadQueueStatus();
            onSettled?.("error");
            return;
          }
        } catch {
          // transient network — keep polling
        }
        if (Date.now() - start > 10 * 60 * 1000) {
          toastError("Publish timed out", `${lotId} — check fb_publish_logs/`);
          onSettled?.("timeout");
          return;
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toastError("Couldn't start publish", e instanceof ApiError ? e.message : String(e));
      onSettled?.("error");
    }
  }, [toastError, toastInfo, toastSuccess, loadQueueStatus]);

  const publish = (item, price, title) => {
    setRowBusy(item.lot_id, "publish");
    // For OC rows the backend needs title + image to materialize the
    // items row on first publish. Parse the first image out of the
    // JSON-array string the API returns.
    let external = null;
    if (item.source === "orderchecker") {
      let firstImage = "";
      try {
        const arr = JSON.parse(item.image_urls || "[]");
        firstImage = Array.isArray(arr) && arr[0] ? String(arr[0]) : "";
      } catch { /* image_urls may be a bare string */ }
      external = {
        // Fall back to the row's stored title only when the row's
        // edit box was blank — the top-level ``title`` arg wins.
        title:     title || item.cleaned_title || item.title || "",
        image_url: firstImage,
        condition: item.condition || "New",
      };
    }
    startPublish(item.lot_id, price, {
      external,
      title,
      onSettled: () => setRowBusy(item.lot_id, null),
    });
  };

  const manualPublish = () => {
    const lotId = manualLot.trim();
    if (!lotId) {
      toastError("Lot ID required", "Enter the lot you want to list");
      return;
    }
    const priceNum = Number(manualPrice);
    const price = Number.isFinite(priceNum) && priceNum > 0 ? priceNum : null;
    setManualBusy(true);
    startPublish(lotId, price, {
      ignoreLocation: manualIgnoreLocation,
      onSettled: (status) => {
        setManualBusy(false);
        if (status === "listed") {
          setManualLot("");
          setManualPrice("");
        }
      },
    });
  };

  const skip = async (item) => {
    setRowBusy(item.lot_id, "skip");
    try {
      await apiPost("/api/fb_skip", { lot_id: item.lot_id });
      toastInfo("Hidden", `${item.lot_id} won't be suggested again`);
      setItems((prev) => prev.filter((c) => c.lot_id !== item.lot_id));
    } catch (e) {
      toastError("Couldn't skip", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRowBusy(item.lot_id, null);
    }
  };

  // Open the Edit & Publish modal for a specific candidate. Snapshot the
  // suggested price the row's inline input would have shown so the modal
  // opens pre-filled with the same number.
  const openEditor = (item) => {
    const suggested = Number(item.fb_value) > 0 ? Number(item.fb_value) : Number(item.value);
    setEditingCandidate(item);
    setEditingPrice(Number.isFinite(suggested) && suggested > 0 ? Math.round(suggested) : null);
  };

  const closeEditor = () => {
    if (editingBusy) return;
    setEditingCandidate(null);
    setEditingPrice(null);
  };

  // Submit from the Edit & Publish modal — same startPublish path the
  // regular Publish button uses, plus the ``overrides`` bag so the CLI
  // applies photo/title/price/description/condition edits in-memory.
  const submitEditor = (overrides) => {
    if (!editingCandidate) return;
    const lotId = editingCandidate.lot_id;
    setEditingBusy(true);
    setRowBusy(lotId, "publish");
    let external = null;
    if (editingCandidate.source === "orderchecker") {
      const firstImage = Array.isArray(overrides.image_urls) && overrides.image_urls[0]
        ? String(overrides.image_urls[0]) : "";
      external = {
        title:     overrides.title || editingCandidate.cleaned_title || editingCandidate.title || "",
        image_url: firstImage,
        condition: overrides.condition || "New",
      };
    }
    startPublish(lotId, overrides.price, {
      external,
      title: overrides.title,
      overrides,
      onSettled: () => {
        setEditingBusy(false);
        setRowBusy(lotId, null);
        setEditingCandidate(null);
        setEditingPrice(null);
      },
    });
  };

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const cutoff = windowDays > 0
      ? Math.floor(Date.now() / 1000) - windowDays * 86400
      : 0;
    const priceFloor = Number(minListingPrice) > 0 ? Number(minListingPrice) : 0;
    return items.filter((it) => {
      if (cutoff > 0) {
        const pd = Number(it.purchase_date);
        if (!Number.isFinite(pd) || pd < cutoff) return false;
      }
      if (priceFloor > 0) {
        // Same precedence the per-row publish input uses to pre-fill
        // the price: fb_value wins when set, else value (eBay list).
        // Rows with neither (e.g. OC rows without a recorded price)
        // are hidden because we can't verify they clear the floor.
        const suggested = Number(it.fb_value) > 0
          ? Number(it.fb_value)
          : Number(it.value);
        if (!Number.isFinite(suggested) || suggested < priceFloor) return false;
      }
      if (!term) return true;
      return (
        (it.title || "").toLowerCase().includes(term) ||
        (it.cleaned_title || "").toLowerCase().includes(term) ||
        (it.lot_id || "").toLowerCase().includes(term) ||
        (it.reasons || []).some((r) => r.toLowerCase().includes(term))
      );
    });
  }, [items, term, windowDays, minListingPrice]);

  const strongCount = items.filter((c) => c.score >= 0.95).length;
  const mediumCount = items.length - strongCount;

  const subtitle = loading
    ? "Scoring inventory…"
    : `${visible.length} suggested${
        strongCount ? ` • ${strongCount} strong` : ""
      }${mediumCount ? ` • ${mediumCount} maybe` : ""}`;

  return (
    <Box>
      {/* Page header — matches OC's standard layout (h1 serif title + secondary subtitle + right-aligned actions) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
            <FacebookOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="h1" sx={{ fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
              Facebook Candidates
            </Typography>
          </Stack>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncOutlinedIcon />}
            variant="contained"
            onClick={() => load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* FB queue history — surfaces any older items left over from the
          old "approve to queue" flow. The page now publishes directly,
          so this is purely informational; the FB Queue page is where
          stragglers get re-published or cleared. */}
      {(queueCounts.queued + queueCounts.exported + queueCounts.error) > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                FB queue (legacy)
              </Typography>
              {queueCounts.queued > 0 && (
                <Chip size="small" label={`${queueCounts.queued} queued`} color="warning" variant="outlined" />
              )}
              {queueCounts.exported > 0 && (
                <Chip size="small" label={`${queueCounts.exported} exported`} color="primary" variant="outlined" />
              )}
              {queueCounts.listed > 0 && (
                <Chip size="small" label={`${queueCounts.listed} listed`} color="success" variant="outlined" />
              )}
              {queueCounts.error > 0 && (
                <Chip size="small" label={`${queueCounts.error} error`} color="error" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Open the FB Queue page to publish or clear these.
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search title, lot, reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={minScore}
            exclusive
            size="small"
            onChange={(_, v) => { if (v !== null) setMinScore(v); }}
          >
            <ToggleButton value={0.95}>Strong only</ToggleButton>
            <ToggleButton value={0.6}>Strong + maybe</ToggleButton>
            <ToggleButton value={0.3}>All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={windowDays}
            exclusive
            size="small"
            onChange={(_, v) => { if (v !== null) setWindowDays(v); }}
          >
            <ToggleButton value={0}>Any time</ToggleButton>
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="number"
            label="Min list price"
            value={minListingPrice || ""}
            onChange={(e) => {
              const v = e.target.value;
              // Empty → 0 (no filter). Non-numeric coerces to 0.
              setMinListingPrice(v === "" ? 0 : Math.max(0, Number(v) || 0));
            }}
            inputProps={{ min: 0, step: 1, 'aria-label': 'Minimum recommended listing price' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={onlyExternal}
                onChange={(e) => setOnlyExternal(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                OrderChecker inventory only
              </Typography>
            }
            sx={{ ml: 0.5, mr: 0 }}
          />
        </Stack>
      </Paper>

      {/* Manual publish — for items the scorer doesn't surface (e.g. a
          keyword we haven't added). The lot still has to exist in
          inventory; backend rejects unknown ids. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
            List a specific lot
          </Typography>
          <TextField
            placeholder="Lot ID"
            value={manualLot}
            onChange={(e) => setManualLot(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
            disabled={manualBusy}
            onKeyDown={(e) => { if (e.key === 'Enter') manualPublish(); }}
          />
          <TextField
            placeholder="Price (optional)"
            value={manualPrice}
            onChange={(e) => setManualPrice(e.target.value)}
            size="small"
            type="number"
            sx={{ width: 140 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 1, step: 1 }}
            disabled={manualBusy}
            onKeyDown={(e) => { if (e.key === 'Enter') manualPublish(); }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={manualIgnoreLocation}
                onChange={(e) => setManualIgnoreLocation(e.target.checked)}
                disabled={manualBusy}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Ignore location (Sold / Trash / empty)
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={manualPublish}
            disabled={manualBusy || !manualLot.trim()}
            startIcon={manualBusy ? <CircularProgress size={14} color="inherit" /> : <CheckOutlinedIcon fontSize="small" />}
          >
            {manualBusy ? "Publishing…" : "Publish to FB"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Defaults to the lot's eBay price if you leave price blank.
          </Typography>
        </Stack>
      </Paper>

      {/* Candidates table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 84 }}>Image</TableCell>
                <TableCell>Candidate</TableCell>
                <TableCell sx={{ width: 180 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <AutoAwesomeOutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {items.length === 0 ? "No candidates" : "Nothing matches"}
                      </Typography>
                      <Typography variant="caption">
                        {items.length === 0
                          ? "Nothing in inventory looks like an obvious local-only listing right now."
                          : "Try widening the score threshold or clearing the search."}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {visible.map((item) => (
                <CandidateRow
                  key={item.lot_id}
                  candidate={item}
                  busy={busy[item.lot_id] || null}
                  onPublish={(price, title) => publish(item, price, title)}
                  onEdit={() => openEditor(item)}
                  onSkipped={() => skip(item)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && visible.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
      <EditPublishDialog
        open={!!editingCandidate}
        candidate={editingCandidate}
        defaultPrice={editingPrice}
        onClose={closeEditor}
        onPublish={submitEditor}
        publishing={editingBusy}
      />
    </Box>
  );
}
